import { useCallback, useEffect, useRef, useState } from 'react';
import { carnetApi, mensajeErrorCarnet } from '@/services/api/carnet.api';
import type { CodigoCarnet, PerfilCarnet } from '@/types/carnet';

/** "1020304050" → "1.020.304.050" — mismo formato que el carnet impreso. */
function formatearDocumento(documento: string): string {
  if (!/^\d+$/.test(documento)) return documento;
  return documento.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function mmss(segundos: number): string {
  const s = Math.max(0, segundos);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function iniciales(nombre: string): string {
  return nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

interface Props {
  empleado: PerfilCarnet;
}

/**
 * Carnet con el QR rotativo.
 *
 * Dos decisiones sostienen la usabilidad en la puerta:
 *
 *  - Al vencerse el código se muestra de inmediato el siguiente, que ya venía
 *    descargado. El cambio es instantáneo y no depende de la señal; la petición
 *    de un par nuevo ocurre después, en segundo plano.
 *  - Si esa petición falla, el carnet sigue en pantalla con un aviso, en vez de
 *    dejar al empleado con un error y sin nada que mostrar.
 */
export function CarnetVivo({ empleado }: Props) {
  const [codigo, setCodigo] = useState<CodigoCarnet | null>(null);
  const [usandoSiguiente, setUsandoSiguiente] = useState(false);
  const [restante, setRestante] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [desincronizado, setDesincronizado] = useState(false);

  // Evita que dos disparos (temporizador y regreso de foco) pidan a la vez.
  const pidiendo = useRef(false);

  const pedirCodigo = useCallback(async (silencioso = false) => {
    if (pidiendo.current) return;
    pidiendo.current = true;

    if (!silencioso) setCargando(true);

    try {
      const nuevo = await carnetApi.codigo();
      setCodigo(nuevo);
      setUsandoSiguiente(false);
      setRestante(nuevo.expira_en_segundos);
      setError(null);
      setDesincronizado(false);
    } catch (err) {
      // En silencioso ya hay un QR en pantalla: se marca como desincronizado
      // pero no se borra, porque sigue siendo válido hasta que expire.
      if (silencioso) setDesincronizado(true);
      else setError(mensajeErrorCarnet(err));
    } finally {
      pidiendo.current = false;
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void pedirCodigo();
  }, [pedirCodigo]);

  // Volver a la pestaña después de un rato: lo mostrado puede estar vencido.
  useEffect(() => {
    const alVolver = () => {
      if (document.visibilityState === 'visible') void pedirCodigo(true);
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [pedirCodigo]);

  // Cuenta regresiva local. Al llegar a cero se pasa al código ya descargado.
  useEffect(() => {
    if (!codigo) return;

    const id = window.setInterval(() => {
      setRestante((prev) => {
        if (prev > 1) return prev - 1;

        if (!usandoSiguiente) {
          setUsandoSiguiente(true);
          void pedirCodigo(true);
          return codigo.paso_segundos;
        }

        // Se agotaron los dos: sin red no hay nada más que mostrar.
        setDesincronizado(true);
        void pedirCodigo(true);
        return 0;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [codigo, usandoSiguiente, pedirCodigo]);

  const png = usandoSiguiente ? codigo?.qr_siguiente_png : codigo?.qr_actual_png;
  const progreso = codigo ? Math.max(0, Math.min(1, restante / codigo.paso_segundos)) : 0;
  const porVencer = restante <= 30 && restante > 0;

  return (
    <div className="w-full max-w-sm">
      {/* ── Tarjeta ───────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-white/10">
        {/* Cabecera */}
        <div className="flex items-center gap-3 bg-[#152C63] px-5 py-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-sm font-bold tracking-wide text-white">
            {iniciales(empleado.nombres)}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold leading-tight text-white">
              {empleado.nombres}
            </p>
            <p className="truncate text-xs leading-tight text-[#A8C2F0]">
              {empleado.cargo ?? 'Colaborador'}
              {empleado.area ? ` · ${empleado.area}` : ''}
            </p>
          </div>
        </div>

        {/* QR — fondo blanco puro y sin nada alrededor: es lo que tiene que leer
            la cámara de la portería, muchas veces a contraluz. */}
        <div className="flex flex-col items-center px-5 pb-1 pt-6">
          <div className="relative aspect-square w-full max-w-[280px]">
            {png ? (
              <img
                src={png}
                alt="Código QR de acceso"
                width={512}
                height={512}
                className="h-full w-full rounded-xl"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-xl bg-slate-100">
                <span className="text-sm text-slate-400">
                  {error ? 'Sin código' : 'Generando…'}
                </span>
              </div>
            )}

            {cargando && !png && (
              <div className="absolute inset-0 animate-pulse rounded-xl bg-slate-200/60" />
            )}
          </div>

          {/* Vigencia */}
          <div className="mt-5 w-full max-w-[280px]">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className={
                  'h-full rounded-full transition-[width] duration-1000 ease-linear ' +
                  (porVencer ? 'bg-amber-500' : 'bg-[#1A56DB]')
                }
                style={{ width: `${progreso * 100}%` }}
              />
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
                Se renueva en
              </span>
              <span
                className={
                  'font-mono text-sm font-semibold tabular-nums ' +
                  (porVencer ? 'text-amber-600' : 'text-slate-600')
                }
              >
                {mmss(restante)}
              </span>
            </div>
          </div>
        </div>

        {/* Documento */}
        <div className="mt-4 flex items-center justify-center gap-2 border-t border-slate-100 px-5 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">
            C.C.
          </span>
          <span className="font-mono text-base font-bold tabular-nums text-[#152C63]">
            {formatearDocumento(empleado.documento)}
          </span>
        </div>

        {/* Pie — mismo mensaje del carnet físico */}
        <div className="bg-[#1A56DB] px-5 py-3 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white">
            Personal e intransferible
          </p>
          <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[#C6D9FA]">
            Presentar en portería
          </p>
        </div>
      </div>

      {/* ── Avisos ────────────────────────────────────────────── */}
      {desincronizado && (
        <p className="mt-4 rounded-xl bg-amber-500/15 px-4 py-3 text-center text-xs leading-relaxed text-amber-200 ring-1 ring-amber-400/30">
          No pudimos renovar el código. Si la portería lo rechaza, conéctate a
          internet y vuelve a intentar.
        </p>
      )}

      {error && !png && (
        <div className="mt-4 rounded-xl bg-red-500/15 px-4 py-3 text-center ring-1 ring-red-400/30">
          <p className="text-xs leading-relaxed text-red-200">{error}</p>
          <button
            type="button"
            onClick={() => void pedirCodigo()}
            className="mt-2 text-xs font-semibold text-white underline underline-offset-4"
          >
            Reintentar
          </button>
        </div>
      )}

      <p className="mt-5 text-center text-[11px] leading-relaxed text-white/45">
        Sube el brillo de la pantalla para que la cámara lo lea más rápido.
      </p>
    </div>
  );
}
